// DTO → view model for the three Summary reports: mass balance, reconciliation, material movement.
import { formatDate, formatDateOrDash } from '@/lib/format/date';
import { formatTextOrDash } from '@/lib/format/text';
import { formatNumber, formatPercentOrDash } from '@/lib/format/number';
import { getTranslation } from '@/lib/utils/i18n';
import { shiftDateInput } from '@/lib/utils';
import { unitSymbol } from '@/lib/mappers/units';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { reconciliationStatusLabel, movementStatusLabel } from '@/config/statuses';

// Mass balance reads as a ledger, so a figure keeps its sign; a missing one is still a dash.
export const signed = (amount) =>
  amount == null ? '-' : `${amount > 0 ? '+' : ''}${formatNumber(amount)}`;

// The date part of a server DateTime, so a UTC midnight never slips a day in the viewer's zone.
const dayOf = (value) => (typeof value === 'string' ? value.slice(0, 10) : '');

const periodOf = (detail) => ({
  name: formatTextOrDash(detail?.periodName),
  startDate: detail?.startDate ?? null,
  endDate: detail?.endDate ?? null,
});

const ghgEmissionsText = (value) => (value == null ? '-' : formatNumber(value));

// What the Filter panel matches on — the raw values, not the formatted cells.
const filterFieldsOf = (row) => ({
  rawMaterialTypeRaw: row.rawMaterialType ?? row.typeOfRawMaterial ?? null,
  countryOfOriginRaw: row.countryOfOrigin ?? null,
  ghgSavingsRaw: row.ghgSavings ?? null,
  ghgEmissionsRaw: row.ghgEmissions ?? null,
});

export const MASS_BALANCE_FIGURES = Object.freeze([
  'openingBalance',
  'inboundQty',
  'allocatedQty',
  'closingBalance',
]);

const figuresOf = (source) =>
  Object.fromEntries(MASS_BALANCE_FIGURES.map((key) => [key, source?.[key] ?? null]));

export const processMassBalanceSummary = (dto) => {
  if (!dto) return null;
  return {
    period: periodOf(dto.periodDetail),
    rows: (dto.rows ?? []).map((row, index) => ({
      id: `${row.typeOfRawMaterial ?? ''}-${row.countryOfOrigin ?? ''}-${index}`,
      rawMaterialType: formatTextOrDash(row.typeOfRawMaterial),
      countryOfOrigin: formatTextOrDash(row.countryOfOrigin),
      ghgSavings: formatPercentOrDash(row.ghgSavings),
      ghgEmissions: ghgEmissionsText(row.ghgEmissions),
      ...filterFieldsOf(row),
      figures: figuresOf(row),
    })),
    totals: figuresOf(dto.totals),
  };
};

// `variance` and its percentage read as one figure, as Figma draws them: "+100 (10%)".
const varianceText = (variance, percentage) => {
  if (variance == null) return '-';
  const percent = percentage == null ? null : formatPercentOrDash(percentage);
  return percent ? `${signed(variance)} (${percent})` : signed(variance);
};

export const processReconciliationSummary = (dto) => {
  if (!dto) return null;
  return {
    period: periodOf(dto.periodDetail),
    rows: (dto.subPeriods ?? []).map((row, index) => {
      const from = dayOf(row.startDate);
      // Sub-periods are [start, end) like every period window, so the last day is end − 1.
      const to = shiftDateInput(dayOf(row.endDate), -1);
      return {
        id: String(row.subPeriodId),
        subPeriodId: row.subPeriodId,
        serialNo: String(index + 1),
        from,
        to,
        reconciliationPeriod: `${formatDateOrDash(from)} - ${formatDateOrDash(to)}`,
        availableClosingBalance: signed(row.availableClosingBalance),
        reconciledQuantity: signed(row.reconciledQuantity),
        adjustedClosingBalance: signed(row.adjustedClosingBalance),
        physicalClosingBalance: signed(row.physicalClosingBalance),
        physicalClosingBalanceRaw: row.physicalClosingBalance ?? null,
        variance: varianceText(row.variance, row.variancePercentage),
        // A null status means no physical balance was entered, which is its own state.
        status:
          row.status == null
            ? getTranslation('INVENTORY_NOT_APPLICABLE')
            : reconciliationStatusLabel(row.status),
        statusCode: row.status ?? null,
        hasPhysicalBalance: row.physicalClosingBalance != null,
      };
    }),
    // The last sub-period's figures, not a sum — iscc-backend's ReconciliationTotals.
    totals: dto.totals
      ? {
          availableClosingBalance: signed(dto.totals.availableClosingBalance),
          reconciledQuantity: signed(dto.totals.reconciledQuantity),
          adjustedClosingBalance: signed(dto.totals.adjustedClosingBalance),
          physicalClosingBalance: signed(dto.totals.physicalClosingBalance),
        }
      : null,
  };
};

// iscc-backend's MOVEMENT_TYPE sends display names; this turns them back into our codes.
const MOVEMENT_CODE_BY_SERVER_NAME = Object.freeze({
  'Forward (Inbound)': 'FORWARD_INBOUND',
  'To receive': 'TO_RECEIVE',
  Received: 'RECEIVED',
  Surplus: 'SURPLUS',
  'Write off': 'WRITE_OFF',
  'Forward (Outbound)': 'FORWARD_OUTBOUND',
  Dispatched: 'DISPATCHED',
  Allocated: 'ALLOCATED',
  'Declaration generated': 'DECLARATION_GENERATED',
});

export const MOVEMENT_SECTIONS = Object.freeze({ inbound: 'inbound', outbound: 'outbound' });

const statusOf = (node) => {
  const code = MOVEMENT_CODE_BY_SERVER_NAME[node.movementType] ?? null;
  return { statusCode: code, status: code ? movementStatusLabel(code) : null };
};

const sectionOf = (node) =>
  node.actionType === MOVEMENT_SECTIONS.outbound
    ? MOVEMENT_SECTIONS.outbound
    : MOVEMENT_SECTIONS.inbound;

// Period maps arrive keyed by the period's index as a string.
const periodAmountsOf = (map) =>
  Object.fromEntries(Object.entries(map ?? {}).map(([index, amount]) => [index, amount ?? null]));

const movementDetailsOf = (node) => ({
  ...statusOf(node),
  section: sectionOf(node),
  rawMaterialType: formatTextOrDash(node.rawMaterialType),
  countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
  ghgSavings: formatPercentOrDash(node.ghgSavings),
  ghgEmissions:
    node.ghgEmissions == null
      ? '-'
      : getTranslation('INVENTORY_GHG_EMISSIONS_VALUE', { value: formatNumber(node.ghgEmissions) }),
  ...filterFieldsOf(node),
});

const TRACKING_CONFLICT = 'CONFLICT';

export const processMaterialMovementSummary = (dto) => {
  if (!dto) return null;
  const hasTracking = Boolean(dto.trackingUnit) && dto.trackingUnitStatus !== TRACKING_CONFLICT;
  return {
    isEmpty: Boolean(dto.isEmpty),
    // Null when no tracking unit is set or the batches disagree on one — the picker then omits it.
    trackingUnit: hasTracking ? unitSymbol(dto.trackingUnit) : null,
    periods: (dto.periods ?? []).map((period) => {
      const from = dayOf(period.start);
      const to = shiftDateInput(dayOf(period.end), -1);
      return {
        id: `period-${period.index}`,
        index: String(period.index),
        label: getTranslation('INVENTORY_COL_PERIOD', {
          from: formatDate(from),
          to: formatDate(to),
        }),
      };
    }),
    rows: (dto.rows ?? []).map((row, index) => ({
      id: `${row.movementType ?? ''}-${index}`,
      ...movementDetailsOf(row),
      amounts: {
        [UNIT_TYPES.declaration]: {
          periods: periodAmountsOf(row.periodQtyDeclaration),
          grandTotal: row.grandTotalDeclaration ?? null,
        },
        [UNIT_TYPES.working]: {
          periods: periodAmountsOf(row.periodQtyWorking),
          grandTotal: row.grandTotalWorking ?? null,
        },
        [UNIT_TYPES.tracking]: {
          periods: periodAmountsOf(row.periodQtyTracking),
          grandTotal: row.grandTotalTracking ?? null,
        },
      },
    })),
    detailed: (dto.detailed ?? []).map((row, index) => ({
      id: `${row.referenceId ?? ''}-${index}`,
      ...movementDetailsOf(row),
      referenceId: formatTextOrDash(row.referenceId),
      declarationNo: formatTextOrDash(row.declarationNo),
      counterpartyName: formatTextOrDash(row.counterpartyName),
      movementDate: formatDateOrDash(row.movementDate),
      quantity: {
        [UNIT_TYPES.declaration]: row.quantityDeclaration ?? null,
        [UNIT_TYPES.working]: row.quantityWorking ?? null,
        [UNIT_TYPES.tracking]: row.quantityTracking ?? null,
      },
    })),
  };
};
